// 점수 모델 (§5 확정 스펙).
//   - 신호별 가중치 → raw_b(0~1)
//   - 비선형 S자 정규화 → behavior_score(0~100)
//   - 내용 배율 → final(0~100)

import type { BehaviorResult, RawSignals } from "./types";

// S자 정규화 상수 (확정).
export const SIGMOID_K = 8; // 가파름
export const SIGMOID_M = 0.45; // 중점

/** 포화 정규화: 0..1, x가 커질수록 1에 수렴. scale에서 약 0.63. */
function saturate(x: number, scale: number): number {
  if (x <= 0) return 0;
  return 1 - Math.exp(-x / scale);
}

function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}

/** 입력 이벤트 타임스탬프 → 리듬·버스트 점수(간격 분산 + 빠른 구간 비율). */
function rhythmScore(timestamps: number[], durationMs: number): number {
  if (timestamps.length < 3 || durationMs <= 0) return 0;
  const gaps: number[] = [];
  for (let i = 1; i < timestamps.length; i++) {
    gaps.push(timestamps[i] - timestamps[i - 1]);
  }
  const mean = gaps.reduce((a, b) => a + b, 0) / gaps.length;
  const variance =
    gaps.reduce((a, g) => a + (g - mean) * (g - mean), 0) / gaps.length;
  const cv = mean > 0 ? Math.sqrt(variance) / mean : 0; // 변동계수
  // 빠른 구간 비율: 100ms 미만 간격(버스트 타이핑)
  const fastRatio = gaps.filter((g) => g < 100).length / gaps.length;
  return clamp01(0.5 * clamp01(cv / 1.5) + 0.5 * fastRatio);
}

/** 텍스트 모드 신호별 정규화 (각 0~1). */
export function normalizeTextSignals(s: RawSignals): Record<string, number> {
  const total = s.insertCount + s.deleteCount;
  return {
    // 감정 마커: 느낌표·물음표·대문자도배·반복도배 합산 후 포화
    emotion: saturate(
      s.exclaimCount * 1.5 + s.questionCount + s.capsRun * 0.5 + s.repeatRun * 0.5,
      8,
    ),
    // 분량: 1 - exp(-len/120)
    length: saturate(s.charCount, 120),
    // 리듬·버스트
    rhythm: rhythmScore(s.eventTimestamps, s.durationMs),
    // 수정·삭제율
    edits: total > 0 ? clamp01(s.deleteCount / total) : 0,
    // 속도: 분당 입력 이벤트 (모바일 오염 때문에 가중치 낮음)
    speed:
      s.durationMs > 0
        ? saturate((s.insertCount / s.durationMs) * 60000, 300)
        : 0,
  };
}

/** 음성 모드 신호별 정규화 (각 0~1). */
export function normalizeVoiceSignals(s: RawSignals): Record<string, number> {
  const v = s.voice;
  return {
    volume: clamp01(v?.peakVolume ?? 0),
    pitch: clamp01(v?.pitchVariance ?? 0),
    speechRate: clamp01(v?.speechRate ?? 0),
    length: saturate(s.charCount, 120),
  };
}

// 가중치 (합 1.0, §5 확정).
const TEXT_WEIGHTS: Record<string, number> = {
  emotion: 0.35,
  length: 0.25,
  rhythm: 0.2,
  edits: 0.1,
  speed: 0.1,
};
const VOICE_WEIGHTS: Record<string, number> = {
  volume: 0.45,
  pitch: 0.25,
  speechRate: 0.2,
  length: 0.1,
};

/** S자 정규화: raw_b(0~1) → 0~100. */
export function sigmoidScore(rawB: number): number {
  return 100 / (1 + Math.exp(-SIGMOID_K * (rawB - SIGMOID_M)));
}

/** 행동 점수 계산. */
export function computeBehavior(s: RawSignals): BehaviorResult {
  // voice 또는 mixed(우세=음성)이고 실제 음성 데이터가 있으면 음성 지표로 채점.
  const isVoice = (s.mode === "voice" || s.mode === "mixed") && !!s.voice;
  const signals = isVoice ? normalizeVoiceSignals(s) : normalizeTextSignals(s);
  const weights = isVoice ? VOICE_WEIGHTS : TEXT_WEIGHTS;
  let rawB = 0;
  for (const key of Object.keys(weights)) {
    rawB += (signals[key] ?? 0) * weights[key];
  }
  rawB = clamp01(rawB);
  return { rawB, behaviorScore: sigmoidScore(rawB), signals };
}

/** 최종 점수: behavior × content_factor, clamp + round. */
export function finalScore(behaviorScore: number, contentFactor: number): number {
  return Math.max(0, Math.min(100, Math.round(behaviorScore * contentFactor)));
}
